import { useContext, type ButtonHTMLAttributes, type CSSProperties, type ReactNode } from 'react';
import { useLocation } from 'wouter';
import { DisplayContext } from './context';

/** Themed surface. `tone` picks the Multi theme's per-card gradient (--c1…--c10). */
export function Card({ tone, className = '', style, children }: { tone: number; className?: string; style?: CSSProperties; children: ReactNode }) {
  return (
    <section className={`card ${className}`} style={{ background: `var(--c${tone}, var(--card))`, ...style }}>
      {children}
    </section>
  );
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return <div className="text-[12px] font-semibold uppercase tracking-eyebrow text-muted">{children}</div>;
}

export function Headline({ children }: { children: ReactNode }) {
  return <h1 className="mt-1 font-serif text-[36px] font-normal leading-[1.1] tracking-[-0.02em]">{children}</h1>;
}

export function ScreenHeader({ eyebrow, title, right }: { eyebrow: ReactNode; title?: ReactNode; right?: ReactNode }) {
  const ctx = useContext(DisplayContext);
  const [location, navigate] = useLocation();
  // With the nav rail hidden, a page still needs a way back.
  const onHome = location === '/display' || location === '/display/';
  const homeButton = ctx && ctx.chromeHidden && !onHome;
  return (
    <header className={`flex flex-none justify-between ${title ? 'items-end' : 'items-center'}`}>
      {homeButton ? (
        <button
          type="button"
          onClick={() => navigate('/display')}
          aria-label="Home"
          className="tap mr-4 flex h-[48px] flex-none items-center rounded-full border pl-3 pr-4 text-[15px] font-medium"
          style={{ borderColor: 'var(--bd)', alignSelf: 'center' }}
        >
          <span className="mr-1 text-[22px] leading-none">‹</span>Home
        </button>
      ) : null}
      <div className="min-w-0 flex-1">
        <Eyebrow>{eyebrow}</Eyebrow>
        {title ? <Headline>{title}</Headline> : null}
      </div>
      {right ? <div className="ml-4 flex-none">{right}</div> : null}
    </header>
  );
}

/** Makes a display-only widget open its page when tapped (long-press still customizes). */
export function TapToOpen({ to, children }: { to: string; children: ReactNode }) {
  const [, navigate] = useLocation();
  return (
    <div
      role="link"
      tabIndex={0}
      className="tap flex h-full min-h-0 w-full min-w-0 flex-col [&>section]:min-h-0 [&>section]:flex-1"
      onClick={() => navigate(to)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') navigate(to);
      }}
    >
      {children}
    </div>
  );
}

export function Pill({ children, tone = 'default' }: { children: ReactNode; tone?: 'default' | 'warn' }) {
  return (
    <div
      className="whitespace-nowrap rounded-full border px-3 py-[7px] text-[12px]"
      style={{ borderColor: tone === 'warn' ? 'var(--now)' : 'var(--bd)', color: tone === 'warn' ? 'var(--now)' : 'var(--muted2)' }}
    >
      {children}
    </div>
  );
}

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'outline' | 'ghost' };

export function Button({ variant = 'outline', className = '', style, ...rest }: BtnProps) {
  const v: CSSProperties =
    variant === 'primary'
      ? { background: 'var(--ink)', color: 'var(--onInk)', borderColor: 'var(--ink)' }
      : variant === 'outline'
        ? { borderColor: 'var(--bd)', color: 'var(--ink)' }
        : { borderColor: 'transparent', color: 'var(--ink)' };
  return (
    <button
      type="button"
      className={`tap inline-flex h-[44px] items-center justify-center whitespace-nowrap rounded-[12px] border px-5 text-[15px] font-medium disabled:opacity-40 ${className}`}
      style={{ ...v, ...style }}
      {...rest}
    />
  );
}

/** Rounded filter / choice chip; the selected chip inverts to ink. */
export function Chip({ selected, onClick, children, label }: { selected: boolean; onClick: () => void; children: ReactNode; label?: string }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={label}
      onClick={onClick}
      className="tap inline-flex h-[44px] items-center rounded-full border px-4 text-[14px]"
      style={
        selected
          ? { background: 'var(--ink)', color: 'var(--onInk)', borderColor: 'var(--ink)' }
          : { background: 'transparent', color: 'var(--ink)', borderColor: 'var(--bd)' }
      }
    >
      {children}
    </button>
  );
}

export function Toggle({ on, onChange, label, sub }: { on: boolean; onChange: (v: boolean) => void; label: string; sub?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      className="tap flex min-h-[64px] w-full items-center justify-between border-t border-line text-left"
    >
      <span className="mr-4">
        <span className="block text-[15px]">{label}</span>
        {sub ? <span className="mt-[3px] block text-[12px] text-muted">{sub}</span> : null}
      </span>
      <span
        className="relative h-[30px] w-[52px] flex-none rounded-[15px]"
        style={{ background: on ? 'var(--ink)' : 'var(--toggleOff)', boxShadow: 'inset 0 0 0 1.5px var(--toggleRing)' }}
      >
        <span
          className="absolute top-[3px] h-[24px] w-[24px] rounded-full"
          style={{ left: on ? 25 : 3, background: on ? 'var(--onInk)' : 'var(--knobOff)', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 150ms' }}
        />
      </span>
    </button>
  );
}

/** Segmented control in the prototype's style. */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  itemWidth,
  label,
}: {
  options: { key: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  itemWidth?: number;
  label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex min-w-0 rounded-[11px] p-[3px]" style={{ background: 'var(--soft)', boxShadow: 'inset 0 0 0 1px var(--softBd)' }}>
      {options.map((o) => {
        const sel = o.key === value;
        return (
          <button
            key={o.key}
            type="button"
            role="radio"
            aria-checked={sel}
            onClick={() => onChange(o.key)}
            className="tap flex h-[38px] min-w-0 items-center justify-center rounded-[8px] px-1 text-[13px]"
            style={{
              flex: itemWidth ? 'none' : 1,
              width: itemWidth,
              background: sel ? 'var(--seg)' : 'transparent',
              color: sel ? 'var(--segFg, var(--ink))' : 'var(--ink)',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Avatar({ initial, size = 24 }: { initial: string; size?: number }) {
  return (
    <span
      className="inline-flex flex-none items-center justify-center rounded-full border border-bd font-semibold"
      style={{ width: size, height: size, fontSize: size <= 24 ? 11 : 12 }}
      aria-hidden
    >
      {initial}
    </span>
  );
}

export function Unavailable({ children }: { children: ReactNode }) {
  return <div className="flex h-full items-center justify-center p-4 text-center text-[14px] text-muted">{children}</div>;
}

/** Modal sheet over a scrim; tapping the scrim closes it. */
export function SheetFrame({ label, onClose, children, width = 460 }: { label: string; onClose: () => void; children: ReactNode; width?: number }) {
  return (
    <div className="cover z-30 flex items-center justify-center" style={{ background: 'rgba(35,37,33,0.32)' }} onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className="max-h-full overflow-y-auto rounded-sheet p-6"
        style={{ width, background: 'var(--sheet)', boxShadow: '0 20px 50px rgba(0,0,0,0.2)', WebkitBackdropFilter: 'var(--blur)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

/** Member chips plus "Family" (null). */
export function WhoChips({
  members,
  value,
  onChange,
}: {
  members: { id: string; display_name: string }[];
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  return (
    <div className="mt-2 flex flex-wrap">
      {[...members.map((m) => ({ id: m.id as string | null, label: m.display_name })), { id: null, label: 'Family' }].map((o) => (
        <div key={o.label} className="mb-2 mr-2">
          <Chip selected={value === o.id} onClick={() => onChange(o.id)}>
            {o.label}
          </Chip>
        </div>
      ))}
    </div>
  );
}

export function FieldLabel({ children }: { children: ReactNode }) {
  return <div className="text-[14px] text-muted2">{children}</div>;
}

/** Round check button used by chores and habits (44pt target, 26px circle). */
export function CheckCircle({ checked, onClick, label, color, disabled }: { checked: boolean; onClick: () => void; label: string; color?: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="tap flex h-[44px] w-[44px] flex-none items-center justify-center disabled:opacity-30"
    >
      <span
        className="flex h-[26px] w-[26px] items-center justify-center rounded-full text-[13px]"
        style={{ border: `1.5px solid ${color || 'var(--ink)'}`, background: checked ? color || 'var(--ink)' : 'transparent', color: 'var(--onInk)' }}
        aria-hidden
      >
        {checked ? '✓' : ''}
      </span>
    </button>
  );
}
